import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { AuthTokens, LoginCredentials } from '../shared/types';
import { apiGet, apiPost, clearAccessToken, getAccessToken, setAccessToken } from '../shared/apiClient';
import { useToast } from './useToast';
import { unsubscribeUserFromPush } from '../utils/pushNotifications';

type SquadraUser = AuthTokens['utente'];

interface AuthContextType {
  user: SquadraUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);
const STORAGE_KEY = 'squadra-session';

function loadStoredUser(): SquadraUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SquadraUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SquadraUser | null>(() => loadStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const persist = useCallback((nextUser: SquadraUser | null, token: string | null) => {
    setUser(nextUser);
    setAccessToken(token);
    if (nextUser && token) localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(loadStoredUser());
      return;
    }
    try {
      const { data } = await apiGet<SquadraUser>('/auth/me');
      setUser(data);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      persist(null, null);
    }
  }, [persist]);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const { data } = await apiPost<AuthTokens>('/auth/login', { ...credentials, portale: 'squadra' });
    // Questo portale è riservato alle squadre: rifiuta gli altri ruoli.
    if (data.utente.ruolo !== 'squadra') {
      persist(null, null);
      clearAccessToken();
      throw { response: { data: { errore: 'Questo portale è riservato alle squadre. Usa il portale operatore.' } } };
    }
    persist(data.utente, data.token);
    toast({ type: 'success', message: `Benvenuto, ${data.utente.nome}` });
  }, [persist, toast]);

  const logout = useCallback(async () => {
    try {
      // Rimuovi la sottoscrizione push dal DB PRIMA del logout,
      // altrimenti il dispositivo resta associato alla vecchia squadra
      await unsubscribeUserFromPush().catch(() => {});
      await apiPost('/auth/logout', {});
    } catch {}
    persist(null, null);
    clearAccessToken();
    toast({ type: 'info', message: 'Logout effettuato' });
    window.location.assign('/login');
  }, [persist, toast]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return context;
}